import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { Volume2, Pencil } from "lucide-react";
import { useTTS } from "../hooks/useTTS";
import { useSEO } from "../hooks/useSEO";
import { KanaStrokeModal } from "../components/KanaStrokeModal";

const KanaPage = () => {
  const [tab, setTab] = useState("hiragana");
  const [data, setData] = useState([]);
  const [flipped, setFlipped] = useState(new Set());
  const [manifest, setManifest] = useState(null);
  const [strokeIndex, setStrokeIndex] = useState(null);
  const { speak: ttsSpeak } = useTTS();

  useSEO({
    title: "Hiragana & Katakana gratuits : Apprendre les kana avec audio | Japonais Pas à Pas",
    description: "Apprendre l'hiragana et le katakana en ligne, gratuitement. 46 hiragana + 46 katakana avec audio natif, cartes interactives et mémorisation espacée.",
    canonical: "https://japonais-pas-a-pas.com/kana",
  });

  // Load the stroke-order manifest once (static file in public/).
  useEffect(() => {
    fetch("/kana-traces/manifest.json")
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setFlipped(new Set());
    setStrokeIndex(null); // close the stroke modal when switching syllabary
    api.get(`/kana/${tab}`).then((r) => setData(r.data.data)).catch(() => {});
  }, [tab]);

  // Ordered manifest entries for the active syllabary + char -> index lookup.
  const strokeList = useMemo(() => (manifest?.[tab] || []), [manifest, tab]);
  const strokeByChar = useMemo(() => {
    const m = new Map();
    strokeList.forEach((e, i) => m.set(e.kana, i));
    return m;
  }, [strokeList]);

  const toggle = (i) => {
    const n = new Set(flipped);
    n.has(i) ? n.delete(i) : n.add(i);
    setFlipped(n);
  };

  const speak = (text, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    ttsSpeak(text);
  };

  const openStroke = (char, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const idx = strokeByChar.get(char);
    if (idx !== undefined) setStrokeIndex(idx);
  };

  const renderTile = (k, key) => {
    const i = k._idx;
    const isFlipped = flipped.has(i);
    const hasStroke = strokeByChar.has(k.char);
    return (
      <div
        key={key}
        onClick={() => toggle(i)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle(i)}
        data-testid={`kana-tile-${k.romaji}`}
        className={`kana-tile aspect-square rounded-xl border-2 relative overflow-visible sm:overflow-hidden transition-all cursor-pointer select-none ${isFlipped ? "border-[#E94A3F] bg-[#E94A3F] text-white shadow-brut-sm" : "border-[#1A2A40]/15 bg-[#FDFBF7] hover:border-[#E94A3F] hover:-translate-y-0.5"} ${isFlipped ? "flipped" : ""}`}
      >
        <div className="kana-inner w-full h-full">
          <div className="kana-face font-jp text-2xl sm:text-3xl md:text-4xl font-bold">{k.char}</div>
          <div className="kana-face kana-back flex-col">
            <div className="font-bold text-sm sm:text-base md:text-lg uppercase">{k.romaji}</div>
            <span
              onClick={(e) => speak(k.char, e)}
              role="button"
              tabIndex={0}
              data-testid={`speak-${k.romaji}`}
              className="mt-1 opacity-90 hover:opacity-100 cursor-pointer"
            >
              <Volume2 className="w-4 h-4" />
            </span>
          </div>
        </div>
        {hasStroke && (
          <button
            type="button"
            onClick={(e) => openStroke(k.char, e)}
            aria-label={`Voir l'ordre des traits de ${k.char}`}
            data-testid={`stroke-${k.romaji}`}
            className="absolute z-20 -top-1.5 -right-1.5 sm:top-auto sm:bottom-0 sm:right-0 w-9 h-9 flex items-center justify-center group/pencil"
          >
            <span
              className={`flex items-center justify-center rounded-full shadow-sm transition-transform group-hover/pencil:scale-110 w-[18px] h-[18px] sm:w-7 sm:h-7 ${isFlipped ? "bg-white text-[#E94A3F]" : "bg-[#E94A3F] text-white"}`}
            >
              <Pencil className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" strokeWidth={2} />
            </span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="washi-bg min-h-screen" data-testid="kana-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Démo gratuite</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[1] text-[#1A2A40] mb-4">
          Les kana, <span className="italic font-light">interactifs.</span>
        </h1>
        <p className="text-lg text-[#4A5568] max-w-2xl mb-10">
          Cliquez une tuile pour révéler la prononciation. Cliquez sur 🔊 pour entendre, et sur{" "}
          <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#E94A3F] text-white align-text-bottom">
            <Pencil className="w-3 h-3" strokeWidth={2} />
          </span>{" "}
          pour voir l'ordre des traits.
        </p>

        {/* Tabs */}
        <div className="inline-flex bg-[#F3F6F1] rounded-2xl p-1.5 mb-10 border-2 border-[#1A2A40]/10" data-testid="kana-tabs">
          {["hiragana", "katakana"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all capitalize ${tab === t ? "bg-[#E94A3F] text-white shadow-brut-sm" : "text-[#1A2A40] hover:bg-[#FDFBF7]"}`}
            >
              {t} {t === "hiragana" ? "ひらがな" : "カタカナ"}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-5 gap-3 sm:gap-3 max-w-2xl" data-testid="kana-grid">
          {(() => {
            const LAYOUT = [
              ["a","i","u","e","o"],
              ["ka","ki","ku","ke","ko"],
              ["sa","shi","su","se","so"],
              ["ta","chi","tsu","te","to"],
              ["na","ni","nu","ne","no"],
              ["ha","hi","fu","he","ho"],
              ["ma","mi","mu","me","mo"],
              ["ya",null,"yu",null,"yo"],
              ["ra","ri","ru","re","ro"],
              ["wa",null,null,null,"wo"],
            ];
            const byRomaji = new Map(data.map((k, idx) => [k.romaji, { ...k, _idx: idx }]));
            const cells = [];
            LAYOUT.forEach((row, rIdx) => {
              row.forEach((romaji, cIdx) => {
                if (!romaji) {
                  cells.push(
                    <div key={`empty-${rIdx}-${cIdx}`} aria-hidden="true" className="aspect-square" data-testid={`kana-empty-${rIdx}-${cIdx}`} />
                  );
                  return;
                }
                const k = byRomaji.get(romaji);
                if (!k) {
                  cells.push(<div key={`missing-${romaji}`} aria-hidden="true" className="aspect-square" />);
                  return;
                }
                cells.push(renderTile(k, romaji));
              });
            });
            // ん / ン sur sa propre rangée
            const nItem = byRomaji.get("n");
            if (nItem) {
              cells.push(renderTile(nItem, "n-tile"));
              // combler la rangée pour préserver l'alignement des colonnes
              for (let c = 1; c < 5; c++) {
                cells.push(<div key={`n-pad-${c}`} aria-hidden="true" className="aspect-square" />);
              }
            }
            return cells;
          })()}
        </div>

        <div className="mt-12 bg-[#FFD166]/30 border-2 border-[#FFD166] rounded-2xl p-6">
          <p className="text-sm text-[#1A2A40]">
            💡 <strong>Astuce :</strong> 5 caractères par jour, 10 jours pour mémoriser les hiragana.
            Inscrivez-vous pour activer les quiz, le mode flash-card et la mémorisation espacée.
          </p>
        </div>

        {/* Attribution KanjiVG */}
        <p className="mt-8 text-xs text-[#A0AEC0]" data-testid="kana-credit">
          Ordre des traits :{" "}
          <a
            href="https://kanjivg.tagaini.net"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#4A5568]"
          >
            KanjiVG (Ulrich Apel)
          </a>
          , CC BY-SA 3.0
        </p>
      </div>

      {strokeIndex !== null && strokeList.length > 0 && (
        <KanaStrokeModal
          list={strokeList}
          index={strokeIndex}
          onIndex={setStrokeIndex}
          onClose={() => setStrokeIndex(null)}
          speak={ttsSpeak}
        />
      )}

      <Footer />
    </div>
  );
};

export default KanaPage;
